/**
 * The 03 sheet plan for a recognition result, as the package will carry it.
 *
 * The API runs the same `planSheets` when it builds the package, and the planner is
 * deterministic, so what the operator sees here is what AutoCAD gets.
 */

import { planSheets, type SheetPlan } from "@plan-forge/sheet-planner";
import type { RecognitionResult } from "./api.ts";

export interface SheetPreview {
  readonly plan: SheetPlan | null;
  /** The planner threw. Distinct from a null plan, which means there was nothing to plan. */
  readonly failed: boolean;
}

// Planning a school takes about a second, so it runs once per result and scale, not per frame.
const previews = new WeakMap<RecognitionResult, Map<number, SheetPreview>>();

export function sheetPreviewFor(result: RecognitionResult, inchesPerPreviewPixel: number): SheetPreview {
  let byScale = previews.get(result);
  if (byScale === undefined) {
    byScale = new Map();
    previews.set(result, byScale);
  }
  let preview = byScale.get(inchesPerPreviewPixel);
  if (preview === undefined) {
    try {
      preview = { plan: planSheets(result, inchesPerPreviewPixel), failed: false };
    } catch (error) {
      console.warn("Sheet planning failed for this result; the 03 preview is unavailable.", error);
      preview = { plan: null, failed: true };
    }
    byScale.set(inchesPerPreviewPixel, preview);
  }
  return preview;
}

export function sheetPlanFor(result: RecognitionResult, inchesPerPreviewPixel: number): SheetPlan | null {
  return sheetPreviewFor(result, inchesPerPreviewPixel).plan;
}

export function sheetSummary(plan: SheetPlan): string {
  const count = plan.sheets.length;
  const letters = plan.sheets.map((sheet) => sheet.areaLetter);
  const areas = count === 1 ? "" : ` (areas ${letters[0]}-${letters[letters.length - 1]})`;
  const buildings = new Set(plan.sheets.map((sheet) => sheet.building)).size;
  const across = buildings > 1 ? ` across ${buildings} buildings` : "";
  return `${count} sheet${count === 1 ? "" : "s"} at 1/8" = 1'-0"${areas}${across}`;
}

/** Preview pixels for a plan point: the inverse of the connector's (x * s, -y * s). */
export function planToPreview(p: { x: number; y: number }, inchesPerPreviewPixel: number): { x: number; y: number } {
  return { x: p.x / inchesPerPreviewPixel, y: -p.y / inchesPerPreviewPixel };
}
